import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import api from '../utils/api';
import { Upload, Trash2, Edit2, Save, X, Plus, BarChart3, Search, Calendar } from 'lucide-react';
import './Samples.css';

const Samples = () => {
  const [activeTab, setActiveTab] = useState('samples');
  const [samples, setSamples] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [genres, setGenres] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [message, setMessage] = useState('');
  
  // Upload form state
  const [uploadCategory, setUploadCategory] = useState('');
  const [uploadGenre, setUploadGenre] = useState('');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showGenreModal, setShowGenreModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newGenreName, setNewGenreName] = useState('');

  // Pagination & search
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubcategory, setFilterSubcategory] = useState('');
  const [filterGenre, setFilterGenre] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');

  // Analytics date range
  const [analyticsStartDate, setAnalyticsStartDate] = useState('');
  const [analyticsEndDate, setAnalyticsEndDate] = useState('');

  useEffect(() => {
    fetchCategories();
    fetchSubcategories();
    fetchGenres();
  }, []);

  useEffect(() => {
    if (activeTab === 'samples') {
      fetchSamples();
    } else if (activeTab === 'analytics') {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, analyticsStartDate, analyticsEndDate]);

  // Debounced search and filter effect
  useEffect(() => {
    if (activeTab === 'samples') {
      const timer = setTimeout(() => {
        setCurrentPage(1); // Reset to first page when filters change
        fetchSamples();
      }, 500); // Wait 500ms after user stops typing

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, filterCategory, filterSubcategory, filterGenre, sortBy, sortOrder]);

  const fetchSamples = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 50,
        search,
        sortBy,
        sortOrder
      });

      if (filterCategory) params.append('category', filterCategory);
      if (filterSubcategory) params.append('subcategory', filterSubcategory);
      if (filterGenre) params.append('genre', filterGenre);

      const response = await api.get(`/samples?${params}`);
      setSamples(response.data.samples);
      setTotalPages(response.data.totalPages);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('Error fetching samples:', error);
      setMessage('Error fetching samples');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/categories');
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await api.get('/subcategories');
      setSubcategories(response.data.subcategories || []);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchGenres = async () => {
    try {
      const response = await api.get('/genres');
      setGenres(response.data.genres || []);
    } catch (error) {
      console.error('Error fetching genres:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      if (analyticsStartDate) params.append('startDate', analyticsStartDate);
      if (analyticsEndDate) params.append('endDate', analyticsEndDate);

      const response = await api.get(`/analytics?${params}`);
      setAnalytics(response.data.analytics);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const clearDateFilters = () => {
    setAnalyticsStartDate('');
    setAnalyticsEndDate('');
  };

  const createCategory = async () => {
    if (!newCategoryName.trim()) return;
    try {
      const response = await api.post('/categories', { name: newCategoryName });
      setCategories([...categories, response.data]);
      setNewCategoryName('');
      setShowCategoryModal(false);
      setMessage('Category created successfully');
    } catch (error) {
      setMessage('Error creating category: ' + (error.response?.data?.error || error.message));
    }
  };

  const createGenre = async () => {
    if (!newGenreName.trim()) return;
    try {
      const response = await api.post('/genres', { name: newGenreName });
      setGenres([...genres, response.data]);
      setNewGenreName('');
      setShowGenreModal(false);
      setMessage('Genre created successfully');
    } catch (error) {
      setMessage('Error creating genre: ' + (error.response?.data?.error || error.message));
    }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    setUploading(true);
    setMessage('');

    // Use bulk upload for multiple files (auto-detects from filename)
    if (acceptedFiles.length > 1) {
      try {
        const formData = new FormData();
        acceptedFiles.forEach(file => {
          formData.append('samples', file);
        });

        const response = await api.post('/samples/bulk-upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const { results } = response.data;
        
        if (results.failed.length > 0) {
          const failedNames = results.failed.map(f => `${f.fileName}: ${f.error}`).join('\n');
          setMessage(`Uploaded ${results.successful.length} of ${acceptedFiles.length} files.\n\nFailed:\n${failedNames}`);
        } else {
          setMessage(`Successfully uploaded ${results.successful.length} sample(s) with auto-detected metadata!`);
        }
        
        // Refresh all lists to include newly created categories/genres/subcategories
        fetchCategories();
        fetchSubcategories();
        fetchGenres();
        fetchSamples();
      } catch (error) {
        setMessage('Error uploading: ' + (error.response?.data?.message || error.message));
      }
    } else {
      // Single file upload - requires category selection
      if (!uploadCategory) {
        setMessage('Please select a category before uploading');
        setUploading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('sample', acceptedFiles[0]);
        formData.append('title', acceptedFiles[0].name.replace(/\.[^/.]+$/, ''));
        formData.append('category', uploadCategory);
        if (uploadGenre) {
          formData.append('genre', uploadGenre);
        }
        formData.append('tags', JSON.stringify([]));

        await api.post('/samples/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setMessage('Successfully uploaded 1 sample');
        fetchSamples();
      } catch (error) {
        setMessage('Error uploading: ' + (error.response?.data?.message || error.message));
      }
    }

    setUploading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCategory, uploadGenre]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.flac', '.aiff', '.ogg', '.m4a']
    },
    multiple: true
  });

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sample?')) {
      return;
    }

    try {
      await api.delete(`/samples/${id}`);
      setMessage('Sample deleted successfully');
      fetchSamples();
    } catch (error) {
      setMessage('Error deleting sample: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEdit = (sample) => {
    setEditingId(sample._id);
    setEditData({
      title: sample.title,
      description: sample.description || '',
      isActive: sample.isActive,
      category: sample.category?._id || '',
      genre: sample.genre?._id || ''
    });
  };

  const handleSave = async (id) => {
    try {
      await api.put(`/samples/${id}`, editData);
      setMessage('Sample updated successfully');
      setEditingId(null);
      fetchSamples();
    } catch (error) {
      setMessage('Error updating sample: ' + (error.response?.data?.message || error.message));
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleTagClick = (type, id) => {
    // Clear other filters and set the clicked one
    if (type === 'category') {
      setFilterCategory(id);
      setFilterSubcategory('');
      setFilterGenre('');
    } else if (type === 'subcategory') {
      setFilterSubcategory(id);
      setFilterCategory('');
      setFilterGenre('');
    } else if (type === 'genre') {
      setFilterGenre(id);
      setFilterCategory('');
      setFilterSubcategory('');
    }
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilterCategory('');
    setFilterSubcategory('');
    setFilterGenre('');
    setSearch('');
    setCurrentPage(1);
  };

  const getActiveFilterName = () => {
    if (filterCategory) {
      const cat = categories.find(c => c._id === filterCategory);
      return cat ? `Category: ${cat.name}` : '';
    }
    if (filterSubcategory) {
      const sub = subcategories.find(s => s._id === filterSubcategory);
      return sub ? `Subcategory: ${sub.name}` : '';
    }
    if (filterGenre) {
      const gen = genres.find(g => g._id === filterGenre);
      return gen ? `Genre: ${gen.name}` : '';
    }
    return '';
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="samples-page">
      <div className="page-header">
        <div>
          <h2>Sample Management</h2>
          <p>{totalCount} total samples</p>
        </div>
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'samples' ? 'active' : ''}`}
            onClick={() => setActiveTab('samples')}
          >
            Samples
          </button>
          <button 
            className={`tab ${activeTab === 'analytics' ? 'active' : ''}`}
            onClick={() => setActiveTab('analytics')}
          >
            <BarChart3 size={16} /> Analytics
          </button>
        </div>
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {activeTab === 'samples' && (
        <>
          <div className="search-bar-container">
            <div className="search-bar">
              <Search size={20} />
              <input
                type="text"
                placeholder="Search samples by title, filename, or description..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Filter and Sort Section */}
          <div className="filter-section">
            <div className="filter-controls">
              <div className="filter-group">
                <label>Filter by Category</label>
                <select 
                  value={filterCategory} 
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat._id} value={cat._id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Filter by Subcategory</label>
                <select 
                  value={filterSubcategory} 
                  onChange={(e) => setFilterSubcategory(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Subcategories</option>
                  {subcategories.map(sub => (
                    <option key={sub._id} value={sub._id}>{sub.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Filter by Genre</label>
                <select 
                  value={filterGenre} 
                  onChange={(e) => setFilterGenre(e.target.value)}
                  className="filter-select"
                >
                  <option value="">All Genres</option>
                  {genres.map(genre => (
                    <option key={genre._id} value={genre._id}>{genre.name}</option>
                  ))}
                </select>
              </div>

              <div className="filter-group">
                <label>Sort By</label>
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                  className="filter-select"
                >
                  <option value="createdAt">Upload Date</option>
                  <option value="title">Title</option>
                  <option value="bpm">BPM</option>
                  <option value="sentCount">Sent Count</option>
                  <option value="listenCount">Listen Count</option>
                  <option value="downloadCount">Download Count</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Order</label>
                <select 
                  value={sortOrder} 
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="filter-select"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>

              {(filterCategory || filterSubcategory || filterGenre || search) && (
                <button onClick={clearFilters} className="clear-filter-btn">
                  <X size={16} /> Clear Filters
                </button>
              )}
            </div>

            {getActiveFilterName() && (
              <div className="active-filter">
                <strong>Filtering by:</strong> {getActiveFilterName()}
              </div>
            )}
          </div>

          <div className="upload-section">
            <div className="category-genre-selectors">
              <div className="selector-group">
                <label>Category *</label>
                <div className="selector-with-button">
                  <select 
                    value={uploadCategory} 
                    onChange={(e) => setUploadCategory(e.target.value)}
                    className="category-select"
                  >
                    <option value="">Select Category</option>
                    {categories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                  <button onClick={() => setShowCategoryModal(true)} className="add-btn" title="Add Category">
                    <Plus size={16} />
                  </button>
                </div>
              </div>

              <div className="selector-group">
                <label>Genre (Optional)</label>
                <div className="selector-with-button">
                  <select 
                    value={uploadGenre} 
                    onChange={(e) => setUploadGenre(e.target.value)}
                    className="genre-select"
                  >
                    <option value="">Select Genre</option>
                    {genres.map(genre => (
                      <option key={genre._id} value={genre._id}>{genre.name}</option>
                    ))}
                  </select>
                  <button onClick={() => setShowGenreModal(true)} className="add-btn" title="Add Genre">
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            </div>

            <div {...getRootProps()} className={`dropzone ${isDragActive ? 'active' : ''}`}>
              <input {...getInputProps()} />
              <Upload size={48} />
              {uploading ? (
                <p>Uploading...</p>
              ) : isDragActive ? (
                <p>Drop the audio files here...</p>
              ) : (
                <>
                  <p>Drag & drop audio files here, or click to select</p>
                  <span>Supports: MP3, WAV, FLAC, AIFF, OGG, M4A</span>
                  
                  {!uploadCategory && <span className="warning">⚠️ Select a category for single file uploads</span>}
                </>
              )}
            </div>
          </div>

          <div className="samples-list">
            {samples.map((sample) => (
              <div key={sample._id} className="sample-card">
                {editingId === sample._id ? (
                  <div className="edit-form">
                    <input
                      type="text"
                      value={editData.title}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      placeholder="Title"
                    />
                    <textarea
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      placeholder="Description"
                      rows={3}
                    />
                    <select 
                      value={editData.category} 
                      onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                    >
                      <option value="">Select Category</option>
                      {categories.map(cat => (
                        <option key={cat._id} value={cat._id}>{cat.name}</option>
                      ))}
                    </select>
                    <select 
                      value={editData.genre} 
                      onChange={(e) => setEditData({ ...editData, genre: e.target.value })}
                    >
                      <option value="">Select Genre</option>
                      {genres.map(genre => (
                        <option key={genre._id} value={genre._id}>{genre.name}</option>
                      ))}
                    </select>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={editData.isActive}
                        onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                    <div className="edit-actions">
                      <button onClick={() => handleSave(sample._id)} className="save-btn">
                        <Save size={16} /> Save
                      </button>
                      <button onClick={() => setEditingId(null)} className="cancel-btn">
                        <X size={16} /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="sample-info">
                      <h3>{sample.title}</h3>
                      <p className="sample-filename">{sample.fileName}</p>
                      {sample.description && <p className="sample-description">{sample.description}</p>}
                      <div className="sample-tags">
                        {sample.category && (
                          <span 
                            className="tag category-tag clickable" 
                            onClick={() => handleTagClick('category', sample.category._id)}
                            title="Click to filter by this category"
                          >
                            {sample.category.name}
                          </span>
                        )}
                        {sample.subcategory && (
                          <span 
                            className="tag subcategory-tag clickable" 
                            onClick={() => handleTagClick('subcategory', sample.subcategory._id)}
                            title="Click to filter by this subcategory"
                          >
                            {sample.subcategory.name}
                          </span>
                        )}
                        {sample.genre && (
                          <span 
                            className="tag genre-tag clickable" 
                            onClick={() => handleTagClick('genre', sample.genre._id)}
                            title="Click to filter by this genre"
                          >
                            {sample.genre.name}
                          </span>
                        )}
                        {sample.bpm && <span className="tag bpm-tag">{sample.bpm} BPM</span>}
                        {sample.key && <span className="tag key-tag">{sample.key}</span>}
                      </div>
                      <div className="sample-meta">
                        <span>{formatFileSize(sample.fileSize)}</span>
                        {sample.packName && <span>📦 {sample.packName}</span>}
                        <span>{sample.isActive ? '✅ Active' : '❌ Inactive'}</span>
                        <span>{new Date(sample.uploadedAt).toLocaleDateString()}</span>
                      </div>
                      <div className="sample-stats">
                        <span className="stat">📤 Sent: {sample.sentCount || 0}</span>
                        <span className="stat">👂 Listens: {sample.listenCount || 0}</span>
                        <span className="stat">⬇️ Downloads: {sample.downloadCount || 0}</span>
                      </div>
                    </div>
                    <div className="sample-actions">
                      <button onClick={() => handleEdit(sample)} className="edit-btn">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(sample._id)} className="delete-btn">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="page-btn"
              >
                Previous
              </button>
              
              <div className="page-numbers">
                {[...Array(totalPages)].map((_, i) => {
                  const page = i + 1;
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 2 && page <= currentPage + 2)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`page-number ${currentPage === page ? 'active' : ''}`}
                      >
                        {page}
                      </button>
                    );
                  } else if (page === currentPage - 3 || page === currentPage + 3) {
                    return <span key={page} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="page-btn"
              >
                Next
              </button>

              <span className="page-info">
                Page {currentPage} of {totalPages} ({totalCount} total)
              </span>
            </div>
          )}

          {samples.length === 0 && !loading && (
            <div className="empty-state">
              <Music size={64} />
              <p>No samples found. Try adjusting your search or upload some audio files!</p>
            </div>
          )}
        </>
      )}

      {activeTab === 'analytics' && (
        <div className="analytics-tab">
          {/* Date Range Filter */}
          <div className="date-range-filter">
            <div className="date-filter-header">
              <Calendar size={20} />
              <h3>Date Range Filter</h3>
            </div>
            <div className="date-inputs">
              <div className="date-input-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={analyticsStartDate}
                  onChange={(e) => setAnalyticsStartDate(e.target.value)}
                />
              </div>
              <div className="date-input-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={analyticsEndDate}
                  onChange={(e) => setAnalyticsEndDate(e.target.value)}
                />
              </div>
              {(analyticsStartDate || analyticsEndDate) && (
                <button onClick={clearDateFilters} className="clear-dates-btn">
                  <X size={16} /> Clear
                </button>
              )}
            </div>
            {(analyticsStartDate || analyticsEndDate) && (
              <div className="date-range-info">
                Showing analytics from{' '}
                {analyticsStartDate || 'beginning'} to{' '}
                {analyticsEndDate || 'present'}
              </div>
            )}
          </div>

          {analytics ? (
            <>
              <div className="analytics-section">
                <h3>Overall Statistics</h3>
                <div className="stats-grid">
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overall.totalSent}</div>
                    <div className="stat-label">Total Sent</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overall.totalListens}</div>
                    <div className="stat-label">Total Listens</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overall.totalDownloads}</div>
                    <div className="stat-label">Total Downloads</div>
                  </div>
                  <div className="stat-card">
                    <div className="stat-value">{analytics.overall.engagementRate}</div>
                    <div className="stat-label">Engagement Rate</div>
                  </div>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Performance by Category</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Samples</th>
                        <th>Sent</th>
                        <th>Listens</th>
                        <th>Downloads</th>
                        <th>Conv. Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.byCategory.map((cat) => (
                        <tr key={cat.categoryId}>
                          <td><strong>{cat.categoryName}</strong></td>
                          <td>{cat.sampleCount}</td>
                          <td>{cat.sentCount}</td>
                          <td>{cat.listenCount}</td>
                          <td>{cat.downloadCount}</td>
                          <td>{cat.listenCount > 0 ? ((cat.downloadCount / cat.listenCount) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Performance by Genre</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Genre</th>
                        <th>Samples</th>
                        <th>Sent</th>
                        <th>Listens</th>
                        <th>Downloads</th>
                        <th>Conv. Rate</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.byGenre.map((genre) => (
                        <tr key={genre.genreId}>
                          <td><strong>{genre.genreName}</strong></td>
                          <td>{genre.sampleCount}</td>
                          <td>{genre.sentCount}</td>
                          <td>{genre.listenCount}</td>
                          <td>{genre.downloadCount}</td>
                          <td>{genre.listenCount > 0 ? ((genre.downloadCount / genre.listenCount) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Top Downloaded Samples</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Sample</th>
                        <th>Category</th>
                        <th>Genre</th>
                        <th>Sent</th>
                        <th>Listens</th>
                        <th>Downloads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topSamples.map((sample) => (
                        <tr key={sample._id}>
                          <td><strong>{sample.title}</strong></td>
                          <td>{sample.category?.name || '-'}</td>
                          <td>{sample.genre?.name || '-'}</td>
                          <td>{sample.sentCount}</td>
                          <td>{sample.listenCount}</td>
                          <td>{sample.downloadCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-section">
                <h3>Top Downloaders</h3>
                <div className="table-container">
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>Subscriber</th>
                        <th>Email</th>
                        <th>Downloads</th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.userDownloads.slice(0, 20).map((user) => (
                        <tr key={user.subscriberId}>
                          <td>{user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '-'}</td>
                          <td>{user.email}</td>
                          <td><strong>{user.downloadCount}</strong></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="loading">Loading analytics...</div>
          )}
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="modal-overlay" onClick={() => setShowCategoryModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Category</h3>
            <input
              type="text"
              value={newCategoryName}
              onChange={(e) => setNewCategoryName(e.target.value)}
              placeholder="Category name"
              onKeyPress={(e) => e.key === 'Enter' && createCategory()}
            />
            <div className="modal-actions">
              <button onClick={createCategory} className="primary-btn">Create</button>
              <button onClick={() => setShowCategoryModal(false)} className="secondary-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Genre Modal */}
      {showGenreModal && (
        <div className="modal-overlay" onClick={() => setShowGenreModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create New Genre</h3>
            <input
              type="text"
              value={newGenreName}
              onChange={(e) => setNewGenreName(e.target.value)}
              placeholder="Genre name"
              onKeyPress={(e) => e.key === 'Enter' && createGenre()}
            />
            <div className="modal-actions">
              <button onClick={createGenre} className="primary-btn">Create</button>
              <button onClick={() => setShowGenreModal(false)} className="secondary-btn">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Music = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 18V5l12-2v13" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="16" r="3" />
  </svg>
);

export default Samples;
